import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle } from "lucide-react";

export const DeleteProjectDialog = ({ project, onClose }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const match = typed.trim() === project.project_number;

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${project.project_id}`, { data: { confirm_number: typed.trim() } });
      toast.success(`Project ${project.project_number} deleted`);
      navigate("/projects");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" />Delete project permanently</DialogTitle>
          <DialogDescription>This removes the project, its phases, actions, evidence documents, notes, approvals, issues, order requests and activity history. This cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm" data-testid="delete-project-summary">
          <div className="font-mono font-semibold text-slate-900">{project.project_number}</div>
          <div className="text-slate-800">{project.title}</div>
          <div className="text-xs text-slate-600">{project.customer_name} / {project.brand}</div>
        </div>
        <div>
          <label className="text-xs text-slate-600">Type the project number <span className="font-mono font-semibold">{project.project_number}</span> to confirm</label>
          <Input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={project.project_number} className="font-mono mt-1" data-testid="delete-project-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-project-cancel-btn">Cancel</Button>
          <Button onClick={remove} disabled={!match || busy} className="bg-red-600 hover:bg-red-700" data-testid="delete-project-confirm-btn"><Trash2 className="h-4 w-4 mr-2" />Delete project</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
